/**
 * Generic Device Analyzer
 *
 * A simplified analyzer that uses basic pattern matching as a fallback
 * when AI extraction fails or has low confidence
 */

import { AnalysisContext, DeviceInfo } from "../types";
import { BaseDeviceAnalyzer } from "./baseAnalyzer";

/**
 * Generic Device Analyzer
 *
 * Provides basic pattern matching for critical device information
 * as a fallback when AI extraction fails
 */
export class GenericDeviceAnalyzer extends BaseDeviceAnalyzer {
  /**
   * Sets a low priority for the generic analyzer
   */
  getPriority(): number {
    return 10; // Low priority - used as fallback
  }

  /**
   * Checks if this analyzer can handle the given context
   * @param context The analysis context
   * @returns True if this analyzer can handle the context
   */
  canAnalyze(context: AnalysisContext): boolean {
    return true; // Can analyze any context as a fallback
  }

  /**
   * Analyzes the context to extract device information using pattern matching
   * @param context The analysis context
   * @returns The extracted device information
   */
  analyze(context: AnalysisContext): Partial<DeviceInfo> {
    // Start with empty result
    const result: Partial<DeviceInfo> = {
      confidence: 30, // Low confidence for pattern matching
    };

    try {
      // Extract basic device information using patterns
      this.extractBasicInfo(context.text, result);
      
      // Extract serial number (high priority for device labels)
      this.extractSerialNumber(context.text, result);
      
      // Extract model information
      this.extractModelInfo(context.text, result);
      
      // Extract OS information
      this.extractOSInfo(context.text, result);
      
      // Extract device label specific information
      this.extractDeviceLabelInfo(context.text, result);
      
      // Extract Android-specific information
      this.extractAndroidInfo(context.text, result);
      
      // Extract iOS-specific information
      this.extractIOSInfo(context.text, result);
      
      // Extract macOS-specific information
      this.extractMacOSInfo(context.text, result);
      
      // Store the result in the context
      context.deviceInfo = result;
      
      return result;
    } catch (error) {
      console.error("Error in generic analyzer:", error);
      return result;
    }
  }

  /**
   * Extracts basic device information from text
   * @param text The text to extract from
   * @param result The result object to populate
   */
  private extractBasicInfo(text: string, result: Partial<DeviceInfo>): void {
    // Extract make/manufacturer
    result.make = this.extractManufacturer(text);

    // Extract model
    result.model = this.extractModel(text);
  }

  /**
   * Extracts a serial number from text
   * @param text The text to analyze
   * @param result The result object to populate
   */
  private extractSerialNumber(text: string, result: Partial<DeviceInfo>): void {
    // Common serial number patterns
    const patterns = [
      /serial\s*number\s*[:#]?\s*([a-zA-Z0-9]{5,})/i,
      /s\/n\s*[:#]?\s*([a-zA-Z0-9]{5,})/i,
      /\b(s\/?n|serial)\b[:\s]*([a-zA-Z0-9]{5,})/i,
    ];

    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match && match[1]) {
        result.serialNumber = match[1].trim();
        break;
      }
    }
  }

  /**
   * Extracts model information from text
   * @param text The text to analyze
   * @param result The result object to populate
   */
  private extractModelInfo(text: string, result: Partial<DeviceInfo>): void {
    // Try to extract model from "Model: X" pattern
    const modelMatch = text.match(/\bmodel\s*[:#]?\s*([a-zA-Z0-9 -]+)/i);
    if (modelMatch && modelMatch[1]) {
      result.model = modelMatch[1].trim();
    }

    // Try to extract model from "Product Name: X" pattern
    const productMatch = text.match(
      /\bproduct\s*name\s*[:#]?\s*([a-zA-Z0-9 -]+)/i
    );
    if (productMatch && productMatch[1]) {
      result.model = productMatch[1].trim();
    }
  }

  /**
   * Extracts OS information from text
   * @param text The text to analyze
   * @param result The result object to populate
   */
  private extractOSInfo(text: string, result: Partial<DeviceInfo>): void {
    // Check for Windows
    if (text.match(/\b(windows|win)\b/i)) {
      // Try to extract Windows version
      const versionMatch = text.match(
        /\b(windows|win)\s+([\d.]+|xp|vista|server\s+\d+)\b/i
      );
      if (versionMatch) {
        result.osVersion = versionMatch[2];
        result.osName = `Windows ${versionMatch[2]}`;
      } else {
        result.osName = "Windows";
      }
    }

    // Check for macOS
    else if (text.match(/\b(macos|mac\s+os)\b/i)) {
      // Try to extract macOS version and name
      const versionMatch = text.match(
        /\b(macos|mac\s+os)\s+([a-z]+)\s*([\d.]+)?\b/i
      );
      if (versionMatch) {
        const osNamePart = versionMatch[2];
        const osVersionPart = versionMatch[3] || "";
        
        result.osVersion = osVersionPart || osNamePart;
        result.osName = `macOS ${osNamePart}${osVersionPart ? " " + osVersionPart : ""}`;
      } else {
        result.osName = "macOS";
      }
    }

    // Check for iOS
    else if (text.match(/\bios\b/i)) {
      // Try to extract iOS version
      const iosVersionPattern = /iOS\s+(\d+(?:\.\d+)*)/i;
      const iosVersionMatch = text.match(iosVersionPattern);
      if (iosVersionMatch) {
        result.osVersion = iosVersionMatch[1];
        result.osName = `iOS ${iosVersionMatch[1]}`;
      } else {
        result.osName = "iOS";
      }
    }

    // Check for Android
    else if (text.match(/\bandroid\b/i)) {
      // Try to extract Android version
      const versionMatch = text.match(/\bandroid\s+([\d.]+)\b/i);
      if (versionMatch) {
        result.osVersion = versionMatch[1];
        result.osName = `Android ${versionMatch[1]}`;
      } else {
        result.osName = "Android";
      }
    }
    
    // Check for Linux
    else if (text.match(/\b(linux|ubuntu|debian|fedora|centos|redhat|red\s+hat)\b/i)) {
      const distroMatch = text.match(/\b(ubuntu|debian|fedora|centos|redhat|red\s+hat)\b/i);
      if (distroMatch) {
        const distro = distroMatch[1].replace(/\s+hat/i, ' Hat');
        const versionMatch = text.match(new RegExp(`\\b${distroMatch[1]}\\s+([\\d.]+)\\b`, 'i'));
        if (versionMatch) {
          result.osVersion = versionMatch[1];
          result.osName = `${distro} ${versionMatch[1]}`;
        } else {
          result.osName = distro;
        }
      } else {
        result.osName = "Linux";
      }
    }
  }

  /**
   * Extracts device label specific information
   * @param text The text to extract from
   * @param result The result object to populate
   */
  private extractDeviceLabelInfo(text: string, result: Partial<DeviceInfo>): void {
    // Extract Dell-specific serial numbers (CN-XXXXXX-XXXXX-XXX-XXXX)
    const dellSerialPattern = /CN-([A-Z0-9]{6})-([A-Z0-9]{5})-([A-Z0-9]{3})-([A-Z0-9]{4})/i;
    const dellSerialMatch = text.match(dellSerialPattern);
    if (dellSerialMatch) {
      result.serialNumber = dellSerialMatch[0];
      result.make = "Dell";
      
      // If we found a Dell serial number on a label, it's likely a peripheral if not already identified
      if (!result.type) {
        // Look for keyboard indicators
        if (/keyboard|kbd|kb/i.test(text)) {
          result.type = "keyboard";
        }
        // Look for mouse indicators
        else if (/mouse|mice/i.test(text)) {
          result.type = "mouse";
        }
        // Look for monitor indicators
        else if (/monitor|display|screen/i.test(text)) {
          result.type = "monitor";
        }
        // Default to unknown if we can't determine the type
        else {
          result.type = "unknown";
        }
      }
    }
    
    // Extract revision information
    const revPattern = /REV\.?\s*([A-Z0-9]+)/i;
    const revMatch = text.match(revPattern);
    if (revMatch) {
      result.revision = revMatch[1];
    }
    
    // Extract manufacturing date
    const datePattern = /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4}/i;
    const dateMatch = text.match(datePattern);
    if (dateMatch) {
      result.manufacturingDate = dateMatch[0];
    }
    
    // Extract part number
    const partNumberPattern = /P\/N:?\s*([A-Z0-9-]+)/i;
    const partNumberMatch = text.match(partNumberPattern);
    if (partNumberMatch) {
      result.partNumber = partNumberMatch[1];
    }
    
    // Extract regulatory information
    const fccPattern = /FCC\s+ID:?\s*([A-Z0-9-]+)/i;
    const fccMatch = text.match(fccPattern);
    if (fccMatch) {
      result.regulatoryInfo = `FCC ID: ${fccMatch[1]}`;
    }
  }

  /**
   * Extracts Android-specific information from text
   * @param text The text to extract from
   * @param result The result object to populate
   */
  private extractAndroidInfo(text: string, result: Partial<DeviceInfo>): void {
    // Check if this is an Android device
    if (result.osName === "Android" || /android version|about phone|about device/i.test(text)) {
      result.osName = "Android";
      
      // Extract Android version
      const androidVersionPattern = /android\s+version\s*:?\s*(\d+(?:\.\d+)*)/i;
      const androidVersionMatch = text.match(androidVersionPattern);
      if (androidVersionMatch) {
        result.androidVersion = androidVersionMatch[1];
        result.osVersion = androidVersionMatch[1];
      }
      
      // Extract security patch level
      const securityPatchPattern = /security\s+patch\s+level\s*:?\s*([A-Za-z]+\s+\d+,\s+\d{4}|\d{4}-\d{2}-\d{2})/i;
      const securityPatchMatch = text.match(securityPatchPattern);
      if (securityPatchMatch) {
        result.securityPatchLevel = securityPatchMatch[1];
      }
      
      // Extract kernel version
      const kernelVersionPattern = /kernel\s+version\s*:?\s*([^\n]+)/i;
      const kernelVersionMatch = text.match(kernelVersionPattern);
      if (kernelVersionMatch) {
        result.kernelVersion = kernelVersionMatch[1].trim();
      }
      
      // Extract build number
      const buildNumberPattern = /build\s+number\s*:?\s*([^\n]+)/i;
      const buildNumberMatch = text.match(buildNumberPattern);
      if (buildNumberMatch) {
        result.buildNumber = buildNumberMatch[1].trim();
      }
      
      // Extract baseband version
      const basebandPattern = /baseband\s+version|baseband\s*:?\s*([^\n]+)/i;
      const basebandMatch = text.match(basebandPattern);
      if (basebandMatch && basebandMatch[1]) {
        result.baseband = basebandMatch[1].trim();
      }
      
      // Extract hardware version
      const hardwareVersionPattern = /hardware\s+version\s*:?\s*([A-Z0-9]+)/i;
      const hardwareVersionMatch = text.match(hardwareVersionPattern);
      if (hardwareVersionMatch) {
        result.hardwareVersion = hardwareVersionMatch[1].trim();
      } else {
        // Try alternative pattern for hardware version (e.g., G925P02)
        const altHardwareVersionPattern = /\b([A-Z][0-9]{3,}P[0-9]{2})\b/i;
        const altHardwareVersionMatch = text.match(altHardwareVersionPattern);
        if (altHardwareVersionMatch) {
          result.hardwareVersion = altHardwareVersionMatch[1];
        }
      }
      
      // Extract model number
      const modelNumberPattern = /model\s+number\s*:?\s*(SM-[A-Z0-9]+(?:\/[A-Z]+)?)/i;
      const modelNumberMatch = text.match(modelNumberPattern);
      if (modelNumberMatch) {
        result.modelNumber = modelNumberMatch[1].trim();
      }
      
      // Extract IMEI
      const imeiPattern = /IMEI\s*:?\s*(\d{15})/i;
      const imeiMatch = text.match(imeiPattern);
      if (imeiMatch) {
        result.imei = imeiMatch[1];
      }
      
      // Extract device name
      const deviceNamePattern = /device\s+name\s*:?\s*([^\n]+)/i;
      const deviceNameMatch = text.match(deviceNamePattern);
      if (deviceNameMatch) {
        result.name = deviceNameMatch[1].trim();
      }
      
      // Extract device type
      if (!result.type) {
        if (/phone|smartphone/i.test(text)) {
          result.type = "mobile";
        } else if (/tablet/i.test(text)) {
          result.type = "tablet";
        }
      }
      
      // Extract manufacturer (if not already set)
      if (!result.make) {
        if (/samsung|galaxy/i.test(text)) {
          result.make = "Samsung";
        } else if (/oneplus/i.test(text)) {
          result.make = "OnePlus";
        } else if (/pixel|google/i.test(text)) {
          result.make = "Google";
        } else if (/huawei/i.test(text)) {
          result.make = "Huawei";
        } else if (/xiaomi|redmi/i.test(text)) {
          result.make = "Xiaomi";
        }
      }
      
      // Extract Google Play system update
      const googlePlayPattern = /google\s+play\s+system\s+update\s*:?\s*([^\n]+)/i;
      const googlePlayMatch = text.match(googlePlayPattern);
      if (googlePlayMatch) {
        result.googlePlaySystemUpdate = googlePlayMatch[1].trim();
      }
    }
  }

  /**
   * Extracts iOS-specific information from text
   * @param text The text to extract from
   * @param result The result object to populate
   */
  private extractIOSInfo(text: string, result: Partial<DeviceInfo>): void {
    // Check if this is an iOS device
    if (result.osName === "iOS" || /ios version|about|iphone|ipad/i.test(text)) {
      result.osName = "iOS";
      result.make = "Apple";
      
      // Extract iOS version
      const iosVersionPattern = /iOS\s+(\d+(?:\.\d+)*)/i;
      const iosVersionMatch = text.match(iosVersionPattern);
      if (iosVersionMatch) {
        result.iosVersion = iosVersionMatch[1];
        result.osVersion = iosVersionMatch[1];
        result.osName = `iOS ${iosVersionMatch[1]}`;
      } else {
        result.osName = "iOS";
      }
      
      // Extract model name
      const modelNamePattern = /model\s+name\s*:?\s*([^\n]+)/i;
      const modelNameMatch = text.match(modelNamePattern);
      if (modelNameMatch) {
        result.modelName = modelNameMatch[1].trim();
        // Also set model if not already set
        if (!result.model) {
          result.model = modelNameMatch[1].trim();
        }
      }
      
      // Extract model number
      const modelNumberPattern = /model\s+number\s*:?\s*(SM-[A-Z0-9]+(?:\/[A-Z]+)?)/i;
      const modelNumberMatch = text.match(modelNumberPattern);
      if (modelNumberMatch) {
        result.modelNumber = modelNumberMatch[1].trim();
      }
      
      // Extract serial number
      const serialNumberPattern = /serial\s+number\s*:?\s*([A-Z0-9]+)/i;
      const serialNumberMatch = text.match(serialNumberPattern);
      if (serialNumberMatch) {
        result.serialNumber = serialNumberMatch[1].trim();
      }
      
      // Extract Wi-Fi address
      const wifiAddressPattern = /wi-?fi\s+address\s*:?\s*([A-F0-9:]+)/i;
      const wifiAddressMatch = text.match(wifiAddressPattern);
      if (wifiAddressMatch) {
        result.wifiAddress = wifiAddressMatch[1].trim();
      }
      
      // Extract Bluetooth address
      const bluetoothAddressPattern = /bluetooth\s*:?\s*([A-F0-9:]+)/i;
      const bluetoothAddressMatch = text.match(bluetoothAddressPattern);
      if (bluetoothAddressMatch) {
        result.bluetoothAddress = bluetoothAddressMatch[1].trim();
      }
      
      // Extract Modem Firmware
      const modemFirmwarePattern = /modem\s+firmware\s*:?\s*([0-9.]+)/i;
      const modemFirmwareMatch = text.match(modemFirmwarePattern);
      if (modemFirmwareMatch) {
        result.modemFirmware = modemFirmwareMatch[1].trim();
      }
      
      // Extract EID
      const eidPattern = /eid\s*:?\s*([0-9]+)/i;
      const eidMatch = text.match(eidPattern);
      if (eidMatch) {
        result.eid = eidMatch[1].trim();
      }
      
      // Extract ICCID
      const iccidPattern = /iccid\s*:?\s*([0-9]+)/i;
      const iccidMatch = text.match(iccidPattern);
      if (iccidMatch) {
        result.iccid = iccidMatch[1].trim();
      }
      
      // Extract IMEI
      const imeiPattern = /imei(?:\s*\(?slot\s*\d+\)?)?:?\s*([0-9\s]+)/i;
      const imeiMatch = text.match(imeiPattern);
      if (imeiMatch) {
        result.imei = imeiMatch[1].replace(/\s+/g, '');
      }
      
      // Extract MEID
      const meidPattern = /meid\s*:?\s*([0-9]+)/i;
      const meidMatch = text.match(meidPattern);
      if (meidMatch) {
        result.meid = meidMatch[1].trim();
      }
      
      // Extract Carrier Lock
      const carrierLockPattern = /carrier\s+lock\s*:?\s*([^\n]+)/i;
      const carrierLockMatch = text.match(carrierLockPattern);
      if (carrierLockMatch) {
        result.carrierLock = carrierLockMatch[1].trim();
      }
      
      // Extract Network Carrier
      const networkCarrierPattern = /carrier\s*:?\s*([^\n]+)/i;
      const networkCarrierMatch = text.match(networkCarrierPattern);
      if (networkCarrierMatch) {
        result.networkCarrier = networkCarrierMatch[1].trim();
      }
      
      // Extract Total Capacity
      const totalCapacityPattern = /capacity\s*:?\s*([0-9]+(?:\.[0-9]+)?\s*[A-Z]+)/i;
      const totalCapacityMatch = text.match(totalCapacityPattern);
      if (totalCapacityMatch) {
        result.totalCapacity = totalCapacityMatch[1].trim();
        // Also set storage if not already set
        if (!result.storage) {
          result.storage = totalCapacityMatch[1].trim();
        }
      }
      
      // Extract Available Capacity
      const availableCapacityPattern = /available\s*:?\s*([0-9]+(?:\.[0-9]+)?\s*[A-Z]+)/i;
      const availableCapacityMatch = text.match(availableCapacityPattern);
      if (availableCapacityMatch) {
        result.availableCapacity = availableCapacityMatch[1].trim();
        // Also set availableStorage if not already set
        if (!result.availableStorage) {
          result.availableStorage = availableCapacityMatch[1].trim();
        }
      }
      
      // Extract device name
      const deviceNamePattern = /name\s*:?\s*([^\n]+)/i;
      const deviceNameMatch = text.match(deviceNamePattern);
      if (deviceNameMatch) {
        result.name = deviceNameMatch[1].trim();
      }
      
      // Extract device type
      if (!result.type) {
        if (/iphone/i.test(text)) {
          result.type = "mobile";
        } else if (/ipad/i.test(text)) {
          result.type = "tablet";
        }
      }
    }
  }

  /**
   * Extracts macOS-specific information from text
   * @param text The text to extract from
   * @param result The result object to populate
   */
  private extractMacOSInfo(text: string, result: Partial<DeviceInfo>): void {
    // Check if this is a macOS device
    if (result.osName === "macOS" || /macos|mac os|macbook|about this mac/i.test(text)) {
      result.osName = "macOS";
      result.make = "Apple";
      
      // Extract macOS version
      const macosVersionPattern = /macos\s+([A-Za-z]+)\s*(?:version)?\s*(\d+(?:\.\d+)*)/i;
      const macosVersionMatch = text.match(macosVersionPattern);
      if (macosVersionMatch) {
        const osNamePart = macosVersionMatch[1];
        const osVersionPart = macosVersionMatch[2];
        result.macOSVersion = `${osNamePart} ${osVersionPart}`;
        result.osVersion = osVersionPart;
        result.osName = `macOS ${osNamePart} ${osVersionPart}`;
      } else {
        // Try a simpler pattern
        const simpleVersionMatch = text.match(/macos\s+([A-Za-z]+)/i);
        if (simpleVersionMatch) {
          const osNamePart = simpleVersionMatch[1];
          result.macOSVersion = osNamePart;
          result.osName = `macOS ${osNamePart}`;
        } else {
          result.osName = "macOS";
        }
      }
      
      // Extract model name (MacBook Air, MacBook Pro, etc.)
      const modelNamePattern = /(?:macbook|imac|mac)\s+(?:air|pro|mini)?/i;
      const modelNameMatch = text.match(modelNamePattern);
      if (modelNameMatch) {
        result.model = modelNameMatch[0].trim();
      }
      
      // Extract model identifier (Mac14,2, etc.)
      const modelIdentifierPattern = /model\s+identifier\s*:?\s*(Mac\d+,\d+)/i;
      const modelIdentifierMatch = text.match(modelIdentifierPattern);
      if (modelIdentifierMatch) {
        result.modelIdentifier = modelIdentifierMatch[1].trim();
      }
      
      // Extract model number (MLY33LL/A, etc.)
      const modelNumberPattern = /model\s+number\s*:?\s*([A-Z0-9]+(?:\/[A-Z]+)?)/i;
      const modelNumberMatch = text.match(modelNumberPattern);
      if (modelNumberMatch) {
        result.modelNumber = modelNumberMatch[1].trim();
      }
      
      // Extract chip information (Apple M2, etc.)
      const chipPattern = /chip\s*:?\s*(Apple\s+M\d+)/i;
      const chipMatch = text.match(chipPattern);
      if (chipMatch) {
        result.appleChip = chipMatch[1].trim();
        result.processor = chipMatch[1].trim();
      }
      
      // Extract memory information
      const memoryPattern = /memory\s*:?\s*(\d+\s*GB)/i;
      const memoryMatch = text.match(memoryPattern);
      if (memoryMatch) {
        result.ram = memoryMatch[1].trim();
      }
      
      // Extract serial number
      const serialNumberPattern = /serial\s+number\s*(?:\(system\))?\s*:?\s*([A-Z0-9]+)/i;
      const serialNumberMatch = text.match(serialNumberPattern);
      if (serialNumberMatch) {
        result.serialNumber = serialNumberMatch[1].trim();
      }
      
      // Extract display information
      const displayTypePattern = /(Built-in[^(]+)(?:\(([^)]+)\))?/i;
      const displayTypeMatch = text.match(displayTypePattern);
      if (displayTypeMatch) {
        result.displayType = displayTypeMatch[1].trim();
        if (displayTypeMatch[2]) {
          const resolutionMatch = displayTypeMatch[2].match(/(\d+\s*x\s*\d+)/);
          if (resolutionMatch) {
            result.displayResolution = resolutionMatch[1].trim();
          }
          
          const sizeMatch = displayTypeMatch[2].match(/(\d+(?:\.\d+)?-inch)/);
          if (sizeMatch) {
            result.displaySize = sizeMatch[1].trim();
          }
        }
      }
      
      // Extract storage information
      const storagePattern = /(\d+(?:\.\d+)?\s*GB)\s+available\s+of\s+(\d+(?:\.\d+)?\s*GB)/i;
      const storageMatch = text.match(storagePattern);
      if (storageMatch) {
        result.availableStorage = storageMatch[1].trim();
        result.totalStorage = storageMatch[2].trim();
        result.storage = storageMatch[2].trim();
      }
      
      // Extract system firmware version
      const firmwareVersionPattern = /system\s+firmware\s+version\s*:?\s*([0-9.]+)/i;
      const firmwareVersionMatch = text.match(firmwareVersionPattern);
      if (firmwareVersionMatch) {
        result.systemFirmwareVersion = firmwareVersionMatch[1].trim();
      }
      
      // Extract OS loader version
      const osLoaderVersionPattern = /os\s+loader\s+version\s*:?\s*([0-9.]+)/i;
      const osLoaderVersionMatch = text.match(osLoaderVersionPattern);
      if (osLoaderVersionMatch) {
        result.osLoaderVersion = osLoaderVersionMatch[1].trim();
      }
      
      // Extract hardware UUID
      const hardwareUUIDPattern = /hardware\s+uuid\s*:?\s*([A-F0-9-]+)/i;
      const hardwareUUIDMatch = text.match(hardwareUUIDPattern);
      if (hardwareUUIDMatch) {
        result.hardwareUUID = hardwareUUIDMatch[1].trim();
      }
      
      // Extract provisioning UDID
      const provisioningUDIDPattern = /provisioning\s+udid\s*:?\s*([A-F0-9-]+)/i;
      const provisioningUDIDMatch = text.match(provisioningUDIDPattern);
      if (provisioningUDIDMatch) {
        result.provisioningUDID = provisioningUDIDMatch[1].trim();
      }
      
      // Extract activation lock status
      const activationLockPattern = /activation\s+lock\s+status\s*:?\s*([^\n]+)/i;
      const activationLockMatch = text.match(activationLockPattern);
      if (activationLockMatch) {
        result.activationLockStatus = activationLockMatch[1].trim();
      }
      
      // Extract total cores and core details
      const totalCoresPattern = /total\s+number\s+of\s+cores\s*:?\s*(\d+)/i;
      const totalCoresMatch = text.match(totalCoresPattern);
      if (totalCoresMatch) {
        result.totalCores = totalCoresMatch[1].trim();
      }
      
      const coreDetailsPattern = /total\s+number\s+of\s+cores\s*:?\s*\d+\s*\(([^)]+)\)/i;
      const coreDetailsMatch = text.match(coreDetailsPattern);
      if (coreDetailsMatch) {
        result.coreDetails = coreDetailsMatch[1].trim();
      }
      
      // Extract device name
      const deviceNamePattern = /name\s*:?\s*([^\n]+)/i;
      const deviceNameMatch = text.match(deviceNamePattern);
      if (deviceNameMatch) {
        result.name = deviceNameMatch[1].trim();
      }
      
      // Extract device type based on model
      if (!result.type) {
        if (/macbook/i.test(text)) {
          result.type = "laptop";
        } else if (/imac/i.test(text)) {
          result.type = "desktop";
        } else if (/mac pro/i.test(text)) {
          result.type = "desktop";
        } else if (/mac mini/i.test(text)) {
          result.type = "desktop";
        }
      }
      
      // Extract year if present (e.g., "M2, 2022")
      const yearPattern = /(?:M\d+,?\s+)?(\d{4})/i;
      const yearMatch = text.match(yearPattern);
      if (yearMatch) {
        result.year = yearMatch[1].trim();
      }
    }
  }

  /**
   * Extracts manufacturer information from text
   * @param text The text to analyze
   * @returns The extracted manufacturer or undefined
   */
  private extractManufacturer(text: string): string | undefined {
    // Common manufacturers
    const manufacturers = [
      "Apple",
      "Microsoft",
      "Dell",
      "HP",
      "Lenovo",
      "Asus",
      "Acer",
      "Samsung",
      "LG",
      "Sony",
      "Toshiba",
      "Huawei",
    ];

    for (const manufacturer of manufacturers) {
      if (text.match(new RegExp(`\\b${manufacturer}\\b`, "i"))) {
        return manufacturer;
      }
    }

    // Try to extract manufacturer from "Manufacturer: X" pattern
    const manufacturerMatch = text.match(
      /\bmanufacturer\s*[:#]?\s*([a-zA-Z0-9 ]+)/i
    );
    if (manufacturerMatch && manufacturerMatch[1]) {
      return manufacturerMatch[1].trim();
    }

    return undefined;
  }

  /**
   * Extracts model information from text
   * @param text The text to analyze
   * @returns The extracted model or undefined
   */
  private extractModel(text: string): string | undefined {
    // Try to extract model from "Model: X" pattern
    const modelMatch = text.match(/\bmodel\s*[:#]?\s*([a-zA-Z0-9 -]+)/i);
    if (modelMatch && modelMatch[1]) {
      return modelMatch[1].trim();
    }

    // Try to extract model from "Product Name: X" pattern
    const productMatch = text.match(
      /\bproduct\s*name\s*[:#]?\s*([a-zA-Z0-9 -]+)/i
    );
    if (productMatch && productMatch[1]) {
      return productMatch[1].trim();
    }

    return undefined;
  }
}
